import {
  Anchor,
  Box,
  Card,
  Center,
  Code,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { BarChart, DonutChart } from '@mantine/charts';
import { useState } from 'react';
import { AutoRefreshIndicator } from '../components/auto-refresh-indicator';
import { Link } from 'react-router-dom';
import {
  AUTO_REFRESH_MS,
  useAiReview,
  useHealth,
  useHumanReview,
  useVolume,
  type StatsRange,
} from '../api/dashboard';
import { DegradedBanner } from '../components/degraded-banner';
import { AnimatedNumber } from '../components/animated-number';
import { MetricCard } from '../components/metric-card';

// Palette for the diagnosis-breakdown donut. Long enough that any
// realistic set of analysis-result categories (typically 3-6) cycles
// through it without running out.
const DONUT_COLORS = [
  'blue.6',
  'green.6',
  'orange.6',
  'red.6',
  'violet.6',
  'cyan.6',
  'pink.6',
  'yellow.6',
];

const RANGE_STORAGE_KEY = 'dashboard.range';

// Read the last-selected dashboard date range from localStorage so
// the dropdown defaults to the operator's previous choice on next
// load. Falls back to '7d' if there's no saved value or the value
// isn't one we recognise.
function loadRange(): StatsRange {
  try {
    const saved = window.localStorage?.getItem(RANGE_STORAGE_KEY);
    if (saved === 'today' || saved === '7d' || saved === '30d') return saved;
  } catch {
    /* localStorage can throw in some browsers — fall through */
  }
  return '7d';
}

function rangeLabel(range: StatsRange): string {
  switch (range) {
    case 'today':
      return 'today';
    case '7d':
      return 'last 7 days';
    case '30d':
      return 'last 30 days';
  }
}

function formatDayLabel(iso: string): string {
  // YYYY-MM-DD -> short weekday + day, e.g. "Mon 12"
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric' });
}

function formatMs(ms: number | null): string {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60_000).toFixed(1)}m`;
}

export function DashboardPage() {
  const [range, setRange] = useState<StatsRange>(loadRange());
  const health = useHealth();
  const volume = useVolume(range);
  const aiReview = useAiReview(range);
  const humanReview = useHumanReview(range);

  const allDegraded = [
    ...(volume.data?.degradedFor ?? []),
    ...(aiReview.data?.degradedFor ?? []),
    ...(humanReview.data?.degradedFor ?? []),
    ...(health.data?.partial ? ['health'] : []),
  ];

  const healthyCount = health.data?.services.filter((s) => s.status === 'healthy').length;
  const healthyTotal = health.data?.services.length;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={2}>Platform overview</Title>
        <Group gap="md" align="center">
          <AutoRefreshIndicator
            // Use the max of the four query timestamps so the countdown
            // tracks the *most recent* completed fetch — any slight
            // skew between the staggered tile refreshes still shows the
            // operator a fresh "Refreshes in Xs" without flicker.
            lastFetchedAt={Math.max(
              health.dataUpdatedAt,
              volume.dataUpdatedAt,
              aiReview.dataUpdatedAt,
              humanReview.dataUpdatedAt,
            )}
            isFetching={
              health.isFetching ||
              volume.isFetching ||
              aiReview.isFetching ||
              humanReview.isFetching
            }
            intervalMs={AUTO_REFRESH_MS}
          />
          <Select
            data={[
              { value: 'today', label: 'Today' },
              { value: '7d', label: 'Last 7 days' },
              { value: '30d', label: 'Last 30 days' },
            ]}
            value={range}
            onChange={(v) => {
              if (!v) return;
              const next = v as StatsRange;
              setRange(next);
              // Persist so the operator's choice survives across
              // navigations / page reloads. Quietly swallow storage
              // errors — Safari incognito throws on .setItem.
              try {
                window.localStorage?.setItem(RANGE_STORAGE_KEY, next);
              } catch {
                /* ignore */
              }
            }}
            w={180}
            aria-label="Date range"
          />
        </Group>
      </Group>

      <DegradedBanner degradedFor={allDegraded} />

      <SimpleGrid cols={{ base: 1, sm: 2, lg: 5 }}>
        <MetricCard
          title="Operational health"
          isLoading={health.isLoading}
          isError={health.isError}
          partial={health.data?.partial}
        >
          <Text fw={700}>
            <AnimatedNumber value={healthyCount} />
            {' / '}
            <AnimatedNumber value={healthyTotal} />
            {' healthy'}
          </Text>
        </MetricCard>

        <MetricCard
          title="Cases"
          isLoading={volume.isLoading}
          isError={volume.isError || (volume.data?.data === null && volume.data?.partial === true)}
          partial={volume.data?.partial}
        >
          <AnimatedNumber fw={700} value={volume.data?.data?.cases.inRange} />
          <Text size="xs" c="dimmed">
            {rangeLabel(range)}
          </Text>
        </MetricCard>

        <MetricCard
          title="AI inferences"
          isLoading={aiReview.isLoading}
          isError={
            aiReview.isError || (aiReview.data?.data === null && aiReview.data?.partial === true)
          }
          partial={aiReview.data?.partial}
        >
          <AnimatedNumber fw={700} value={aiReview.data?.data?.inferences.totalInferences} />
          <Text size="xs" c="dimmed">
            {rangeLabel(range)}
          </Text>
        </MetricCard>

        <MetricCard
          title="AI failures"
          isLoading={aiReview.isLoading}
          isError={
            aiReview.isError || (aiReview.data?.data === null && aiReview.data?.partial === true)
          }
          partial={aiReview.data?.partial}
        >
          <AnimatedNumber
            fw={700}
            c={(aiReview.data?.data?.inferences.failureInRange ?? 0) > 0 ? 'red' : undefined}
            value={aiReview.data?.data?.inferences.failureInRange}
          />
          <Text size="xs" c="dimmed">
            {rangeLabel(range)}
          </Text>
        </MetricCard>

        <MetricCard
          title="Human review queue"
          isLoading={humanReview.isLoading}
          isError={
            humanReview.isError ||
            (humanReview.data?.data === null && humanReview.data?.partial === true)
          }
          partial={humanReview.data?.partial}
        >
          <AnimatedNumber fw={700} value={humanReview.data?.data?.queue.open} />
          <Text size="xs" c="dimmed">
            open — avg {formatMs(humanReview.data?.data?.avgTimeToDecisionMs24h ?? null)}
          </Text>
        </MetricCard>
      </SimpleGrid>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        <Card withBorder>
          <Stack gap="xs">
            <Title order={5}>Cases per day ({rangeLabel(range)})</Title>
            {(() => {
              const buckets = volume.data?.data?.casesPerDay ?? [];
              const orgs = volume.data?.data?.casesPerDayOrgs ?? [];
              const hasActivity = buckets.some((b) => b.total > 0);
              if (volume.isLoading) {
                return (
                  <Center mih={220}>
                    <Text c="dimmed" size="sm">
                      Loading…
                    </Text>
                  </Center>
                );
              }
              if (!hasActivity) {
                return (
                  <Center mih={220}>
                    <Text c="dimmed" size="sm">
                      No case activity in this window.
                    </Text>
                  </Center>
                );
              }
              // Build the chart series — one stacked entry per org —
              // and pivot each per-day bucket into a flat row keyed by
              // orgId so recharts can read each series directly.
              const series = orgs.map((o, i) => ({
                name: o.orgId,
                label: o.name,
                color: DONUT_COLORS[i % DONUT_COLORS.length],
              }));
              // Per-org totals across the visible window — surfaced
              // next to each org name in the custom legend so admins
              // can read share at a glance without hovering bars.
              const totalsByOrg = new Map<string, number>();
              for (const row of buckets) {
                for (const orgId of Object.keys(row.byOrg)) {
                  totalsByOrg.set(orgId, (totalsByOrg.get(orgId) ?? 0) + (row.byOrg[orgId] ?? 0));
                }
              }
              const data = buckets.map((row) => {
                const flat: Record<string, string | number> = {
                  day: formatDayLabel(row.date),
                };
                for (const o of orgs) flat[o.orgId] = row.byOrg[o.orgId] ?? 0;
                return flat;
              });
              // Built-in `withLegend` wraps the swatch + label onto
              // separate lines when an org name is long. Custom
              // legend matches the donut chart's row layout.
              return (
                <Stack gap="xs">
                  <BarChart
                    h={220}
                    data={data}
                    dataKey="day"
                    type="stacked"
                    series={series}
                    tickLine="y"
                    withTooltip={false}
                    // Mantine's BarChart hardcodes `isAnimationActive: false`
                    // on the underlying recharts <Bar>; spreading `barProps`
                    // last is how we re-enable the bar growth animation so
                    // the chart eases between values on auto-refresh.
                    barProps={{ isAnimationActive: true, animationDuration: 600 }}
                  />
                  <Group gap="md" wrap="wrap">
                    {series.map((s) => (
                      <Group key={s.name} gap={6} wrap="nowrap">
                        <Box
                          w={10}
                          h={10}
                          style={{
                            background: `var(--mantine-color-${s.color.replace('.', '-')})`,
                            borderRadius: 2,
                            flexShrink: 0,
                          }}
                        />
                        <Text size="xs">
                          {s.label}{' '}
                          <Text component="span" size="xs" c="dimmed">
                            ({totalsByOrg.get(s.name) ?? 0})
                          </Text>
                        </Text>
                      </Group>
                    ))}
                  </Group>
                </Stack>
              );
            })()}
          </Stack>
        </Card>

        <Card withBorder>
          <Stack gap="xs">
            <Title order={5}>AI diagnosis breakdown</Title>
            {aiReview.isLoading ? (
              <Center mih={220}>
                <Text c="dimmed" size="sm">
                  Loading…
                </Text>
              </Center>
            ) : (aiReview.data?.data?.analysisResultBreakdown ?? []).length === 0 ? (
              <Center mih={220}>
                <Text c="dimmed" size="sm">
                  No AI diagnoses yet.
                </Text>
              </Center>
            ) : (
              (() => {
                const donutData = (aiReview.data?.data?.analysisResultBreakdown ?? []).map(
                  (row, i) => ({
                    name: row.result,
                    value: row.count,
                    color: DONUT_COLORS[i % DONUT_COLORS.length],
                  }),
                );
                return (
                  <Group align="center" wrap="nowrap" gap="md">
                    <DonutChart
                      h={240}
                      w={240}
                      size={200}
                      thickness={32}
                      withTooltip={false}
                      data={donutData}
                      // Same trick as the BarChart — Mantine hardcodes
                      // `isAnimationActive: false` on the recharts
                      // <Pie>, but spreads `pieProps` last so we can
                      // re-enable the sweep animation here.
                      pieProps={{ isAnimationActive: true, animationDuration: 600 }}
                    />
                    <Stack gap={4} style={{ flex: 1 }}>
                      {donutData.map((d) => (
                        <Group key={d.name} gap="xs" wrap="nowrap">
                          <Box
                            w={10}
                            h={10}
                            style={{
                              background: `var(--mantine-color-${d.color.replace('.', '-')})`,
                              borderRadius: 2,
                              flexShrink: 0,
                            }}
                          />
                          <Text size="xs" style={{ flex: 1 }}>
                            {d.name}
                          </Text>
                          <Text size="xs" c="dimmed">
                            {d.value}
                          </Text>
                        </Group>
                      ))}
                    </Stack>
                  </Group>
                );
              })()
            )}
          </Stack>
        </Card>
      </SimpleGrid>

      <Stack gap="xs">
        <Title order={4}>Per organisation ({rangeLabel(range)})</Title>
        <Table withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Organisation</Table.Th>
              <Table.Th style={{ width: 120 }}>Cases</Table.Th>
              <Table.Th style={{ width: 120 }}>Drill-down</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(volume.data?.data?.perOrg ?? []).map((org) => (
              <Table.Tr key={org.orgId}>
                <Table.Td>{org.name}</Table.Td>
                <Table.Td>{org.count}</Table.Td>
                <Table.Td>
                  <Anchor component={Link} to={`/orgs/${org.orgId}`}>
                    open
                  </Anchor>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>

      <Stack gap="xs">
        <Title order={4}>Per organisation × product ({rangeLabel(range)})</Title>
        {(volume.data?.data?.perOrgProduct ?? []).length === 0 ? (
          <Text c="dimmed" size="sm">
            No (organisation, product) activity in this window.
          </Text>
        ) : (
          <Table withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Organisation</Table.Th>
                <Table.Th>Product</Table.Th>
                <Table.Th style={{ width: 120 }}>Cases</Table.Th>
                <Table.Th style={{ width: 120 }}>Drill-down</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(volume.data?.data?.perOrgProduct ?? []).map((row) => (
                <Table.Tr key={`${row.orgId}-${row.productCode}`}>
                  <Table.Td>{row.orgName}</Table.Td>
                  <Table.Td>
                    <Code>{row.productCode}</Code>
                  </Table.Td>
                  <Table.Td>{row.count}</Table.Td>
                  <Table.Td>
                    <Anchor component={Link} to={`/orgs/${row.orgId}`}>
                      open
                    </Anchor>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>

      <Stack gap="xs">
        <Title order={4}>Recent AI failures ({rangeLabel(range)})</Title>
        {(aiReview.data?.data?.recentFailures ?? []).length === 0 ? (
          <Text c="dimmed" size="sm">
            No AI failures in this window.
          </Text>
        ) : (
          <Table withTableBorder highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 180 }}>When</Table.Th>
                <Table.Th>Reason</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(aiReview.data?.data?.recentFailures ?? []).map((f) => (
                <Table.Tr key={f.at}>
                  <Table.Td>
                    <Text size="sm" title={f.at}>
                      {new Date(f.at).toLocaleString()}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{f.reason}</Text>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Stack>
    </Stack>
  );
}
